import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Store, Gavel, Check, X, Clock, ArrowUpRight, TrendingDown } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getListings, getMyMarket, placeBid, acceptBid, cancelListing } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const statusLabel = {
  active: 'Open', pending_settlement: 'Sale pending', settled: 'Settled', cancelled: 'Cancelled',
  accepted: 'Accepted', rejected: 'Rejected', withdrawn: 'Withdrawn',
};
const BADGE_CLS = {
  active: 'bg-farmway-gold text-farmway-green',
  settled: 'bg-farmway-leaf/20 text-farmway-leaf-bright',
};

const Badge = ({ s }) => (
  <span className={`rounded-full px-3 py-1 font-display text-[11px] font-bold uppercase tracking-wider ${BADGE_CLS[s] || 'bg-white/10 text-farmway-cream/70'}`}>
    {statusLabel[s] || s}
  </span>
);

export default function Marketplace() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [mine, setMine] = useState({ listings: [], bids: [] });
  const [bidTarget, setBidTarget] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [l, m] = await Promise.all([getListings(), getMyMarket()]);
      setListings(l);
      setMine(m);
    } catch (e) { console.error('Failed to load marketplace data', e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitBid = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid bid amount');
    try {
      await placeBid(bidTarget.id, amt);
      toast.success('Bid placed');
      setBidTarget(null); setAmount('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not place bid'); }
  };

  const onAccept = async (listingId, bidId) => {
    try { const r = await acceptBid(listingId, bidId); toast.success(r.message || 'Bid accepted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not accept bid'); }
  };
  const onCancel = async (listingId) => {
    if (!window.confirm('Cancel this listing?')) return;
    try { await cancelListing(listingId); toast.success('Listing cancelled'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not cancel'); }
  };

  return (
    <div className="min-h-screen bg-farmway-green pt-28">
      <div className="container-fw pb-24">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="eyebrow">Secondary marketplace</span>
            <h1 className="mt-3 display-xl text-4xl text-farmway-cream sm:text-5xl">Trade holdings</h1>
            <p className="mt-3 max-w-2xl text-farmway-cream/70">Buy and sell investor positions at valuation or below. Funds settle off-platform; ownership transfers on Farmway once an admin confirms settlement.</p>
          </div>
          <Link to="/dashboard" className="btn-outline">My portfolio <ArrowUpRight className="h-4 w-4" /></Link>
        </div>

        <Tabs defaultValue="browse" className="mt-10">
          <TabsList data-tour="market-tabs" className="bg-farmway-green-mid/50">
            <TabsTrigger value="browse" className="data-[state=active]:bg-farmway-gold data-[state=active]:text-farmway-green"><Store className="mr-2 h-4 w-4" />Browse</TabsTrigger>
            <TabsTrigger value="mine" className="data-[state=active]:bg-farmway-gold data-[state=active]:text-farmway-green"><Gavel className="mr-2 h-4 w-4" />My activity</TabsTrigger>
          </TabsList>

          {/* BROWSE */}
          <TabsContent value="browse" className="mt-8" data-tour="market-grid">
            {loading ? <p className="text-farmway-cream/50">Loading…</p> : listings.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/15 bg-farmway-green-mid/20 p-12 text-center text-farmway-cream/60">No listings on the market right now.</div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {listings.map((l) => (
                  <div key={l.id} className="overflow-hidden rounded-2xl border border-white/10 bg-farmway-green-mid/40">
                    <div className="relative h-40">
                      {l.image && <img src={l.image} alt="" className="h-full w-full object-cover" />}
                      <div className="absolute inset-0 bg-gradient-to-t from-farmway-green-dark/90 to-transparent" />
                      <div className="absolute left-4 top-4"><Badge s={l.status} /></div>
                      {l.isOwn && <span className="absolute right-4 top-4 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-farmway-cream">Your listing</span>}
                      <p className="absolute bottom-3 left-4 font-display text-lg font-bold text-farmway-cream">{l.projectName}</p>
                    </div>
                    <div className="p-5">
                      <div className="grid grid-cols-2 gap-3">
                        <div><p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Valuation</p><p className="font-display text-lg font-bold text-farmway-cream">{money(l.valuation)}</p></div>
                        <div><p className="text-[10px] uppercase tracking-wider text-farmway-cream/50">Ask price</p><p className="font-display text-lg font-bold text-farmway-gold">{money(l.askPrice)}</p></div>
                      </div>
                      <div className="mt-3 flex items-center gap-3 text-xs text-farmway-cream/60">
                        <span className="flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5 text-farmway-leaf" />{l.units} units</span>
                        <span>{l.bidsCount} bid{l.bidsCount === 1 ? '' : 's'}</span>
                        {l.topBid > 0 && <span>top {money(l.topBid)}</span>}
                        {l.avgBid > 0 && l.bidsCount > 1 && <span>avg {money(l.avgBid)}</span>}
                      </div>
                      {l.insights?.salesCount > 0 && (
                        <div data-testid={`market-insights-${l.id}`} className="mt-3 rounded-lg border border-farmway-leaf/20 bg-farmway-leaf/10 px-3 py-2 text-[11px] leading-relaxed text-farmway-cream/70">
                          <span className="font-display font-bold uppercase tracking-wider text-farmway-leaf-bright">Market insight</span><br />
                          Last sale {money(l.insights.lastSalePrice)}{l.insights.lastSaleDate ? ` · ${new Date(l.insights.lastSaleDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}` : ''} · {l.insights.salesCount} past sale{l.insights.salesCount === 1 ? '' : 's'} · avg {money(l.insights.avgSalePrice)}
                        </div>
                      )}
                      {l.status === 'active' && !l.isOwn && (
                        <button onClick={() => { setBidTarget(l); setAmount(String(l.askPrice)); }} className="btn-gold mt-4 w-full"><Gavel className="h-4 w-4" /> Place bid</button>
                      )}
                      {l.status === 'pending_settlement' && <p className="mt-4 flex items-center gap-2 text-xs text-farmway-cream/60"><Clock className="h-4 w-4 text-farmway-gold" /> Sale pending settlement</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* MY ACTIVITY */}
          <TabsContent value="mine" className="mt-8 space-y-10">
            <div>
              <h3 className="font-display text-lg font-bold text-farmway-cream">My listings</h3>
              {mine.listings.length === 0 ? <p className="mt-3 text-farmway-cream/50">You have no listings. List a holding from your portfolio.</p> : (
                <div className="mt-4 space-y-4">
                  {mine.listings.map((l) => (
                    <div key={l.id} className="rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {l.image && <img src={l.image} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                          <div>
                            <p className="font-display font-bold text-farmway-cream">{l.projectName}</p>
                            <p className="text-xs text-farmway-cream/60">Ask {money(l.askPrice)} · valuation {money(l.valuation)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3"><Badge s={l.status} />
                          {['active', 'pending_settlement'].includes(l.status) && <button onClick={() => onCancel(l.id)} className="text-xs text-farmway-cream/50 hover:text-red-400">Cancel</button>}
                        </div>
                      </div>
                      {l.bids && l.bids.length > 0 && (
                        <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                          {l.bids.map((b) => (
                            <div key={b.id} className="flex items-center justify-between gap-3 text-sm">
                              <span className="text-farmway-cream/80">{b.bidderName}: <b className="text-farmway-cream">{money(b.amount)}</b></span>
                              {l.status === 'active' && b.status === 'active' ? (
                                <button onClick={() => onAccept(l.id, b.id)} className="inline-flex items-center gap-1 rounded-full bg-farmway-gold px-3 py-1 text-[11px] font-bold uppercase text-farmway-green"><Check className="h-3 w-3" />Accept</button>
                              ) : <Badge s={b.status} />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-farmway-cream">My bids</h3>
              {mine.bids.length === 0 ? <p className="mt-3 text-farmway-cream/50">You haven&apos;t placed any bids yet.</p> : (
                <div className="mt-4 space-y-3">
                  {mine.bids.map((b) => (
                    <div key={b.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-farmway-green-mid/40 p-4 text-sm">
                      <span className="text-farmway-cream/80">{b.listing?.projectName || 'Listing'}: <b className="text-farmway-cream">{money(b.amount)}</b></span>
                      <Badge s={b.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={Boolean(bidTarget)} onOpenChange={(o) => !o && setBidTarget(null)}>
        <DialogContent className="max-w-md border-white/10 bg-farmway-green-dark text-farmway-cream">
          <DialogHeader>
            <span className="eyebrow">Place a bid</span>
            <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">{bidTarget?.projectName}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitBid} className="space-y-4">
            <div className="rounded-xl bg-white/5 p-4 text-sm text-farmway-cream/70">Ask price <b className="text-farmway-gold">{money(bidTarget?.askPrice)}</b> · valuation {money(bidTarget?.valuation)}</div>
            {bidTarget?.insights?.salesCount > 0 && (
              <p className="text-xs text-farmway-cream/60">This farm last traded at <b className="text-farmway-leaf-bright">{money(bidTarget.insights.lastSalePrice)}</b> ({bidTarget.insights.salesCount} past sale{bidTarget.insights.salesCount === 1 ? '' : 's'}, avg {money(bidTarget.insights.avgSalePrice)}).</p>
            )}
            <div className="space-y-1.5">
              <Label className="text-farmway-cream/80">Your bid (USD)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="border-white/15 bg-white/5 text-farmway-cream focus-visible:ring-farmway-gold" />
            </div>
            <button type="submit" className="btn-gold w-full"><Gavel className="h-4 w-4" /> Submit bid</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
